import { Injectable } from "@angular/core";
import { PatientActionsEntry } from "@apps/patient-core/src/app/data_model/patient-actions";
import { CacheService } from "./cache.service";
import { I_PatientActions_SessionStorage } from "./abstracts/patient-actions.service";
import { Constants } from "@apps/patient-core/constants";
import { E_Patient_Actions_Type } from "@backend/graph/patient_actions/patient-action-base";

export type PatientLastActionCompleted = {
  type: E_Patient_Actions_Type;
};

@Injectable({
  providedIn: "root",
})
/**
 * Extracted from BasePatientActionsService to avoid cyclic dependencies
 * Responsible for caching patient actions in session storage
 */
export class PatientActionsCacheService {
  constructor(private _cacheService: CacheService) {}

  public clear(): void {
    this._cacheService.deleteSession(Constants.PATIENT_ACTIONS_SESSION_STORAGE_KEY);
  }

  public get isExpired(): boolean {
    return this._expiry <= Date.now();
  }

  private get _expiry(): number {
    const actions_storage = this._sessionItem;
    return actions_storage?.expiry || 0;
  }

  private get _sessionItem(): I_PatientActions_SessionStorage {
    const sessionItem = JSON.parse(this._cacheService.getSession(Constants.PATIENT_ACTIONS_SESSION_STORAGE_KEY) || "{}");

    if (sessionItem?.actions) sessionItem.actions = sessionItem.actions?.map((a) => new PatientActionsEntry(a, a.skipped));
    return sessionItem;
  }

  public get patientActions(): Array<PatientActionsEntry> {
    const actions_storage = this._sessionItem;
    return actions_storage?.actions || [];
  }

  public set patientActions(actions: Array<PatientActionsEntry>) {
    this.cachePatientActions(actions);
  }

  public get lastActionCompleted(): PatientLastActionCompleted | undefined {
    return this._getLastActionCompleted();
  }

  public cachePatientActions(actions: Array<PatientActionsEntry>, actionCompleted?: E_Patient_Actions_Type) {
    const storage_item: I_PatientActions_SessionStorage = {
      actions,
      expiry: new Date().getTime() + 60 * 60 * 1000, // 1 hour
    };

    if (actionCompleted) {
      this._setActionCompleted({
        type: actionCompleted,
      });
    } else {
      this._cacheService.deleteSession(Constants.PATIENT_ACTION_COMPLETED_SESSION_STORAGE_KEY);
    }

    // We add a custom expiry of 1 hour so that we don't have to fetch the patient actions again for an hour. We refetch again after an hour to check if anything has changed externally
    this._cacheService.setSession(Constants.PATIENT_ACTIONS_SESSION_STORAGE_KEY, JSON.stringify(storage_item));
  }

  private _getLastActionCompleted(): PatientLastActionCompleted | undefined {
    return this._cacheService.getJsonSession(Constants.PATIENT_ACTION_COMPLETED_SESSION_STORAGE_KEY) || undefined;
  }

  private _setActionCompleted(actionCompleted: PatientLastActionCompleted): void {
    this._cacheService.setJsonSession(
      Constants.PATIENT_ACTION_COMPLETED_SESSION_STORAGE_KEY,
      actionCompleted,
      this._cacheService.getRelativeTtl(Constants.PATIENT_ACTION_COMPLETED_SESSION_EXPIRY)
    );
  }
}
